import type { GetStocksPayload, GetStocksResult, RatioSpec, StockSeries } from '@jums-dashboard/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { callFunction } from '@/lib/callableClient';

import { resolveAsset, type StocksWidgetConfig } from './config';

const RANGE = '5d';
const INTERVAL = '15m';

interface StocksRequest {
  payload: GetStocksPayload;
  /** Map of asset id → the series key it should read (yahoo symbol or ratio id). */
  symbolByAssetId: Record<string, string>;
}

/** Resolve the configured watchlist into a single batched request. */
function buildRequest(config: StocksWidgetConfig): StocksRequest {
  const symbols = new Set<string>();
  const ratios: RatioSpec[] = [];
  const symbolByAssetId: Record<string, string> = {};

  for (const entry of config.watchlist) {
    const asset = resolveAsset(entry);
    if (!asset) continue;
    if (asset.kind === 'ratio' && asset.ratioOf) {
      ratios.push({ id: asset.id, num: asset.ratioOf[0], den: asset.ratioOf[1] });
      symbols.add(asset.ratioOf[0]);
      symbols.add(asset.ratioOf[1]);
      symbolByAssetId[asset.id] = asset.id; // ratio series is keyed by its id
    } else if (asset.yahooSymbol) {
      symbols.add(asset.yahooSymbol);
      symbolByAssetId[asset.id] = asset.yahooSymbol;
    }
  }

  return {
    payload: { symbols: [...symbols], range: RANGE, interval: INTERVAL, ratios },
    symbolByAssetId,
  };
}

async function fetchStocks(request: StocksRequest): Promise<Record<string, StockSeries>> {
  const result = await callFunction<GetStocksPayload, GetStocksResult>('getStocks', request.payload);
  const bySymbol: Record<string, StockSeries> = {};
  for (const series of result.series) bySymbol[series.symbol] = series;

  const byAssetId: Record<string, StockSeries> = {};
  for (const [assetId, symbol] of Object.entries(request.symbolByAssetId)) {
    const series = bySymbol[symbol];
    if (series) byAssetId[assetId] = series;
  }
  return byAssetId;
}

/**
 * Fetches every configured asset in one callable invocation and returns the
 * per-asset {@link StockSeries} keyed by asset id, so the tile and detail modal
 * can render/switch assets instantly.
 */
export default function useStocks(config: StocksWidgetConfig) {
  const request = useMemo(() => buildRequest(config), [config]);
  const symbolsKey = useMemo(() => {
    const syms = [...request.payload.symbols].sort().join(',');
    const rats = (request.payload.ratios ?? [])
      .map((r) => r.id)
      .sort()
      .join(',');
    return `${syms}|${rats}`;
  }, [request]);

  const enabled = request.payload.symbols.length > 0 || (request.payload.ratios?.length ?? 0) > 0;

  return useQuery({
    queryKey: ['stocks', 'yahoo', symbolsKey],
    queryFn: () => fetchStocks(request),
    enabled,
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}
